import { type ReactNode, createContext, useContext, useMemo, useState } from 'react'

/**
 * App-wide cart item-count, used by the top-bar cart icon badge (screens.md
 * §1 global chrome — cart lives in the header, not the left rail). This is a
 * deliberately tiny store, NOT a second source of truth for the cart: the
 * Cart screen (`CartPage`, `GET /cart`) remains authoritative and PUSHES its
 * count here whenever it loads/mutates the cart, and every other add/remove
 * path (catalog "Add", RFQ→cart, reorder, checkout, Home bulk-import, …)
 * pushes its own post-mutation count the same way.
 *
 * The count is COMBINED — part/BOM lines PLUS requested value-added services
 * (`cart.lines.length + (cart.requestedServices?.length ?? 0)`) — everywhere
 * it's pushed, so the badge never contradicts the Cart screen's own "N
 * items" subtitle or `CartServicesSection`'s count (UID-423, ADR-0127 §C/§H).
 *
 * UID-423 (ADR-0127 §H) — SUPERSEDES this store's earlier "we do NOT fire a
 * global `GET /cart` on app mount" decision: `<AppShell>`'s
 * `useHydrateCartCount` now fires exactly ONE authenticated-only `GET /cart`
 * per session (never on `/login`/`/signup`/onboarding, which route outside
 * `<AppShell>` — and the hook itself additionally gates on
 * `auth.status === 'authenticated'`), so the badge no longer sits
 * `undefined` until the buyer's first cart visit. `lineCount` can still be
 * `undefined` briefly (before that hydrate resolves, or if it fails — see
 * the hook's doc comment for the "leave it untouched" failure posture) or if
 * `<CartCountProvider>` isn't mounted at all (`useCartCount()`'s inert
 * fallback).
 */
interface CartCountValue {
  /** `undefined` = not yet known this session; a number once the cart has loaded. */
  readonly lineCount: number | undefined
  setLineCount(count: number | undefined): void
}

/**
 * The single source of truth for the badge-count invariant (UID-423,
 * ADR-0127 §C/§H): the top-bar cart badge — and every screen's own "N items"
 * copy that must agree with it — counts part/BOM lines PLUS requested
 * value-added services. Every call site that computes this count (catalog
 * add-to-cart, `CartPage`, `CheckoutPage`, `usePartsLibrary`'s reorder,
 * `useHydrateCartCount`) must go through this helper rather than inlining
 * `lines.length + (requestedServices?.length ?? 0)` again, so the formula
 * can only change in one place.
 *
 * Takes the minimal shape rather than the full `CartView` so callers with a
 * partial/derived cart-like object (or `undefined`, before the cart has
 * loaded) can still use it without an intermediate cast.
 */
export function combinedItemCount(
  cart:
    | { readonly lines: readonly unknown[]; readonly requestedServices?: readonly unknown[] }
    | undefined,
): number {
  return (cart?.lines.length ?? 0) + (cart?.requestedServices?.length ?? 0)
}

const CartCountContext = createContext<CartCountValue | undefined>(undefined)

export function CartCountProvider({ children }: { children: ReactNode }) {
  const [lineCount, setLineCount] = useState<number | undefined>(undefined)
  const value = useMemo<CartCountValue>(() => ({ lineCount, setLineCount }), [lineCount])
  return <CartCountContext.Provider value={value}>{children}</CartCountContext.Provider>
}

/**
 * Tolerates being called with no provider mounted (returns an inert value), so
 * a component that reads the count — e.g. `TopBar` — can be rendered in
 * isolation in tests without every such test having to wrap the provider.
 */
export function useCartCount(): CartCountValue {
  return useContext(CartCountContext) ?? INERT
}

const INERT: CartCountValue = { lineCount: undefined, setLineCount: () => {} }
